import Controller, { inject as controller } from '@ember/controller';
import { action } from '@ember/object';
import { alias } from '@ember/object/computed';
import { inject as service } from '@ember/service';
import { task } from 'ember-concurrency';
import RouterService from '@ember/routing/router-service';
import FacebookLeadAdsForm from 'cio/models/facebook-lead-ads-form';
import LogModel from 'cio/models/log';
import CampaignModel from 'cio/models/campaign';
import AccountService from 'cio/services/account';
import FacebookLeadAdsService from 'cio/services/facebook-lead-ads';
import AnalyticsService from 'cio/services/analytics';
import CustomForm from 'cio/models/custom-form';
import CustomFormsService from 'cio/services/custom-forms';
import { isCustomForm, isFacebookForm } from 'cio/utils/forms';
import EnvironmentService from 'cio/services/environment';
import { IdentifierKey } from 'cio/utils/identifiers';
import joinWords from 'cio/utils/join-words';
import IntegrationsFormsController from 'cio/pods/integrations/forms/controller';
import { FormIntegrationDetailPanes } from 'cio/components/forms/custom/integration-details';

export default class IntegrationsFormsFormDetailsIndex extends Controller {
  @service account!: AccountService;
  @service analytics!: AnalyticsService;
  @service customForms!: CustomFormsService;
  @service environment!: EnvironmentService;
  @service facebookLeadAds!: FacebookLeadAdsService;
  @service router!: RouterService;

  @controller('integrations/forms') baseController!: IntegrationsFormsController;

  readonly FormIntegrationDetailPanes = FormIntegrationDetailPanes;
  sendingImage = new URL('cio/assets/images/illustrations/sending.png', import.meta.url).toString();

  conversionTracking = false;
  editingFormSettings = false;
  isRescanning = false;
  isDisconnecting = false;
  isConnecting = false;

  @alias('model.form') form!: FacebookLeadAdsForm | CustomForm;
  @alias('model.logs') logs!: LogModel[];
  @alias('model.campaigns') campaigns!: CampaignModel[];

  get isFacebookForm() {
    return isFacebookForm(this.form);
  }

  get submittedVia(): { label: string; action: () => unknown } | undefined {
    // don't show "Submitted via ..." info for Facebook forms:
    if (isFacebookForm(this.form)) {
      return;
    }

    if (this.form.isTypeform) {
      return {
        label: 'Typeform',
        action: () => this.baseController.showCustomFormDataDetailsFor(FormIntegrationDetailPanes.Typeform, null),
      };
    } else if (this.form.isJotform) {
      return {
        label: 'Jotform',
        action: () => this.baseController.showCustomFormDataDetailsFor(FormIntegrationDetailPanes.Jotform, null),
      };
    } else if (this.form.isScannedForm) {
      return {
        label: 'JS snippet',
        action: () =>
          this.baseController.showCustomFormDataDetailsFor(FormIntegrationDetailPanes.JavaScriptSnippet, this.form),
      };
    } else if (this.form.isApiForm) {
      return {
        label: 'API',
        action: () =>
          this.baseController.showCustomFormDataDetailsFor(FormIntegrationDetailPanes.FormsEndpoint, this.form),
      };
    } else if (this.form.isHTMLActionForm) {
      return {
        label: 'Form action attribute',
        action: () =>
          this.baseController.showCustomFormDataDetailsFor(FormIntegrationDetailPanes.HTMLAction, this.form),
      };
    }

    return;
  }

  get saveSubmitError() {
    if (!this.form.hasDirtyAttributes) {
      return 'No changes are made.';
    } else if (this.form.hasDuplicateFieldMapping) {
      return "Can't map more than one field to the same profile attribute.";
    } else if (this.form.hasCioIdFieldMapping) {
      return `You cannot map to ${IdentifierKey.CioId}.`;
    }

    if (isCustomForm(this.form)) {
      if (this.form.hasReservedFieldMapping) {
        return "Can't map a reserved form field name.";
      } else if (!this.form.identifierAttrMaps.length) {
        return `Must map to an identifier attribute (${joinWords(
          this.environment.current.enabledIdentifiers,
          ', ',
          ' or ',
        )})`;
      } else if (this.form.identifierAttrMaps.length > 1) {
        return 'Can map to only one identifier attribute (email or id).';
      } else if (this.form.hasDuplicateFieldKey) {
        return "Can't have more than one form field with the same key.";
      } else if (this.form.isApiForm && this.form.hasEmptyFieldMapping) {
        // Validate only for API custom forms:
        // Facebook and scanned custom forms have un-editable set of mappings, which can be only disabled/enabled (by UI checkbox)
        return "Form field and person attribute can't be empty.";
      }
    }

    return null;
  }

  saveTask = task({ drop: true }, async () => {
    if (isFacebookForm(this.form)) {
      await this.facebookLeadAds.updateFormSettings(this.form);
    } else {
      await this.customForms.updateFormSettings(this.form);
    }
    this.set('editingFormSettings', false);
  });

  @action startEdit() {
    this.set('editingFormSettings', true);
    if (isCustomForm(this.form)) {
      this.form.set('error', ''); // modifying form mappings clears any current form submit error
    }
  }

  @action
  cancelEdit() {
    this.form.rollbackAttributes();
    this.set('editingFormSettings', false);
  }

  @action
  toggleConversionTracking() {
    this.set('conversionTracking', !this.conversionTracking);
  }

  @action connectStart() {
    this.analytics.track('forms', 'manage_form_started', {
      environmentId: this.environment.current.id,
      action: 'Connecting',
      formType: this.form.formType,
      formId: this.form.formId,
      api: isCustomForm(this.form) && this.form.isApiForm,
      scanned: isCustomForm(this.form) && this.form.isScannedForm,
    });

    this.set('isConnecting', true);
  }

  @action disconnectStart() {
    this.analytics.track('forms', 'manage_form_started', {
      environmentId: this.environment.current.id,
      action: 'Disconnecting',
      formType: this.form.formType,
      formId: this.form.formId,
      api: isCustomForm(this.form) && this.form.isApiForm,
      scanned: isCustomForm(this.form) && this.form.isScannedForm,
    });

    this.set('isDisconnecting', true);
  }

  @action async updateCustomFormName(name: string) {
    // typeguard for TS:
    if (isFacebookForm(this.form)) {
      return;
    }

    this.form.set('name', name);

    try {
      await this.customForms.updateFormSettings(this.form);
    } catch (e) {
      this.form.rollbackAttributes();
      throw e;
    }
  }

  setDefaults() {
    this.setProperties({
      editingFormSettings: false,
      conversionTracking: false,
      isRescanning: false,
      isDisconnecting: false,
      isConnecting: false,
    });
  }

  @action
  closeDisconnectModal() {
    this.set('isDisconnecting', false);

    // redirect back to the forms list if the form was disconnected:
    if (this.form.isDisconnected) {
      this.router.transitionTo('integrations.forms.general.index', {
        queryParams: {
          status: 'all',
        },
      });
    }
  }
}

declare module '@ember/controller' {
  interface Registry {
    'integrations/forms/form_details/index': IntegrationsFormsFormDetailsIndex;
  }
}
